<script lang="ts">
import type { TableColumnConfig } from './internal';

type SlotKeys = 'headerCell' | 'filterCell' | 'cell' | 'createCell' | 'editCell' | 'summaryCell';
type RequiredPropsKeys = 'columnId' | 'toCsvString';

type Slots<
  T extends Record<string, unknown>,
  Column extends keyof T | (string & {}),
  T_keyof_T = T[keyof T],
> = Pick<TableColumnConfig<T, Column, T_keyof_T>, SlotKeys>;
type Props<
  T extends Record<string, unknown>,
  Column extends keyof T | (string & {}),
  T_keyof_T = T[keyof T],
> = Omit<TableColumnConfig<T, Column, T_keyof_T>, SlotKeys>;

type RequiredProps<
  T extends Record<string, unknown>,
  Column extends keyof T | (string & {}),
  T_keyof_T = T[keyof T],
> = Pick<Props<T, Column, T_keyof_T>, RequiredPropsKeys>;
type OptionalProps<
  T extends Record<string, unknown>,
  Column extends keyof T | (string & {}),
  T_keyof_T = T[keyof T],
> = Omit<Partial<Props<T, Column, T_keyof_T>>, RequiredPropsKeys>;

export type NxTableColumnProps<
  T extends Record<string, unknown>,
  Column extends keyof T | (string & {}),
  T_keyof_T = T[keyof T],
> = RequiredProps<T, Column, T_keyof_T> & OptionalProps<T, Column, T_keyof_T>;
</script>

<script
  setup
  lang="ts"
  generic="
    T extends Record<string, unknown>,
    Column extends keyof T | (string & {}) = keyof T | (string & {}),
    T_keyof_T = T[keyof T]
  "
>
import { inject, onMounted, onUnmounted, watch } from 'vue';
import { type ColumnRegistryUpdate, registerColumKey } from './internal';

// Every column filters by default: the header funnel needs no filterCell —
// its checklist values derive from `toCsvString` (or `filterValueSource`).
// A missing filterCell just leaves that column's QUICK-row cell empty.
const props = withDefaults(defineProps<NxTableColumnProps<T, Column, T_keyof_T>>(), {
  filterable: true,
  resizeable: true,
  reorderable: true,
  sortable: true,
  editable: true,
  tabNavigationDisabled: false,
  inactivePointerInteractive: false,
  eagerEditCell: false,
  defaultVisible: true,
  layout: 'row',
  align: 'start',
});

const slots = defineSlots<Slots<T, Column, T_keyof_T>>();

const { registerColum, updateRegisterValue } = inject(registerColumKey) as unknown as {
  registerColum: (column: TableColumnConfig<T, Column, T_keyof_T>) => () => void;
  updateRegisterValue: (column: ColumnRegistryUpdate<T, Column, T_keyof_T>) => () => void;
};
let unregisterColum: (() => void) | undefined;
onMounted(() => {
  const column = { ...props, ...slots };
  // Forward reactive summary reads directly instead of rewriting the registry on
  // callback identity changes. Inline callbacks are fresh on every parent render;
  // watching them and mutating the registry would recursively render the table.
  for (const key of [
    'summary',
    'summaryValue',
    'summaryFormat',
    'summaryShowSign',
    'summarySignTone',
    'summaryUnit',
  ] as const) {
    Object.defineProperty(column, key, { enumerable: true, get: () => props[key] });
  }
  Object.defineProperty(column, 'summaryCell', { enumerable: true, get: () => slots.summaryCell });
  unregisterColum = registerColum(column);
});

onUnmounted(() => {
  unregisterColum?.();
  unregisterColum = undefined;
});

// The registered config is a spread taken in `onMounted`; a header a host
// renames at runtime (the positions hero reads "Artikel" once the description
// moves into its own column) has to follow the prop like layout and align do.
watch(
  () => [props.layout, props.align, props.displayName],
  () => {
    updateRegisterValue({
      columnId: props.columnId,
      layout: props.layout,
      align: props.align,
      displayName: props.displayName,
    });
  }
);

/**
 * What the filter UI reads, as VALUES: the pickable options and the text each of
 * them carries.
 *
 * The registered config is a spread taken in `onMounted`, and everything an ERP
 * grid knows about its enums arrives after that — the allowed values with the
 * schema, the labels with the translations. The filter popover therefore kept
 * the first render's copy while the cells followed the props: a blank checklist
 * next to a filled dropdown, or `partial_delivery` in the checklist beside
 * "Teillieferung" in the cells — the very divergence `optionText` /
 * `filterOptionLabel` exist to remove (I-1016).
 *
 * The fingerprint compares what the two produce, never their identity. Every
 * grid passes them as expressions — `column.typeDetails.allowedValues`, an
 * inline `(value) => enumValueLabel(...)` — so a fresh array and a fresh closure
 * arrive on EVERY parent render. Re-registering on identity would write into the
 * config on every render of the table that reads it, and the read would schedule
 * the next render: the recursive update this replaced. Comparing the rendered
 * values terminates instead — a re-created label function that says the same
 * words writes nothing.
 */
function filterOptionsFingerprint(): string {
  const options = props.filterOptions ?? [];
  const label = props.filterOptionLabel;
  return JSON.stringify([options, label ? options.map((value) => label(value)) : null]);
}

watch(filterOptionsFingerprint, () => {
  updateRegisterValue({
    columnId: props.columnId,
    filterOptions: props.filterOptions,
    filterOptionLabel: props.filterOptionLabel,
  });
});
</script>

<!-- eslint-disable-next-line vue/valid-template-root -->
<template>
  <!-- renderless component -->
</template>
